import React, { useState } from 'react';
import { PatchCard } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../../shared';
import { PATCH_BADDIE, PATCH_LETTER_A, PATCH_SIGN } from './patchDemoData';

export const meta = {
  component: PatchCard,
  title: 'Patch Card',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  figmaNode: '4184:19937',
  description:
    'Catalogue card for one patch: square thumbnail, zoom hint, title and price, plus a quantity badge once the patch has been added. Fluid width — let the grid set the column (min 140px once a title or price shows; image-only cards shrink to the cell). Hover is desktop-pointer only.',
  props: [
    ['image', 'string', '—', 'Patch image — contained on the gray thumbnail'],
    ['imageAlt', 'string', "''", 'Alt text for the image'],
    ['title', 'string', '—', 'Patch name; the details row renders when title or price is set'],
    ['price', 'string', '—', 'Formatted price, e.g. "49 kr" — rendered through the DS Price'],
    ['originalPrice', 'string', '—', 'Formatted strike-through price, e.g. "79 kr" — the Price turns discounted'],
    ['quantity', 'number', '0', 'Quantity already added — the top-left badge shows when > 0'],
    ['onClick', 'func', '—', 'Card click — makes the whole card interactive (hover + focus ring)'],
    ['onZoom', 'func', '—', 'Turns the zoom icon into its own button; without it the icon is decorative'],
    ['zoomLabel', 'string', "'Zoom'", 'Accessible name of the zoom button'],
    ['onRemove', 'func', '—', 'Remove handler — an X on the top-right corner (the zoom icon moves top-left)'],
    ['removeLabel', 'string', "'Remove'", 'Accessible name of the remove button'],
    ['size', "'default' | 'large'", "'default'", 'large — roomier padding and gap for a design / preview card'],
    ['thumbBackground', 'string', '—', 'CSS background of the thumbnail well (e.g. a render on the stage backdrop)'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'Patch catalogue grid',
      code: `import { PatchCard } from '@ajat/design-system/Configurator';

<div className="grid grid-cols-2 lg:grid-cols-3 gap-medium-s">
  {patches.map((patch) => (
    <PatchCard
      key={patch.id}
      image={patch.image}
      imageAlt={patch.name}
      title={patch.name}
      price={patch.priceLabel}
      quantity={countInCart(patch.id)}
      onClick={() => addPatch(patch)}
      onZoom={() => openPreview(patch)}
    />
  ))}
</div>`,
    },
  ],
};

export default function PatchCardPage() {
  const [counts, setCounts] = useState({ baddie: 2, letter: 0, sign: 0 });
  const add = (id) => setCounts((c) => ({ ...c, [id]: c[id] + 1 }));

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States" hint="hover the interactive card">
        <DemoRow>
          <Demo caption="DEFAULT (STATIC)">
            <div className="w-[206px]"><PatchCard image={PATCH_BADDIE} imageAlt="Baddie patch" title="Baddie" /></div>
          </Demo>
          <Demo caption="INTERACTIVE">
            <div className="w-[206px]"><PatchCard image={PATCH_BADDIE} imageAlt="Baddie patch" title="Baddie" onClick={() => {}} /></div>
          </Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Details" hint="title, price and quantity are data-driven">
        <DemoRow>
          <Demo caption="TITLE + PRICE">
            <div className="w-[206px]"><PatchCard image={PATCH_BADDIE} imageAlt="Baddie patch" title="Baddie" price="49 kr" /></div>
          </Demo>
          <Demo caption="QUANTITY">
            <div className="w-[206px]"><PatchCard image={PATCH_BADDIE} imageAlt="Baddie patch" title="Baddie" price="49 kr" quantity={2} /></div>
          </Demo>
          <Demo caption="NO DETAILS">
            <div className="w-[206px]"><PatchCard image={PATCH_BADDIE} imageAlt="Baddie patch" /></div>
          </Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Catalogue grid" hint="click a card to add it — the badge counts up">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-medium-s max-w-2xl">
          <PatchCard image={PATCH_BADDIE} imageAlt="Baddie patch" title="Baddie" price="49 kr" quantity={counts.baddie} onClick={() => add('baddie')} onZoom={() => {}} />
          <PatchCard image={PATCH_LETTER_A} imageAlt="Letter A patch" title="Letter A" price="29 kr" quantity={counts.letter} onClick={() => add('letter')} onZoom={() => {}} />
          <PatchCard image={PATCH_SIGN} imageAlt="Sign patch" title="Sign" price="39 kr" quantity={counts.sign} onClick={() => add('sign')} onZoom={() => {}} />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
